import React, { useEffect, useState } from 'react'
import { Button, Input, InputNumber, Modal, Spin } from 'antd'
import styled from 'styled-components/macro'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import {
  buildToleranceModalDraft,
  buildToleranceSavePlan,
  formatTolerancePounds,
  MAX_INVOICE_MATCH_TOLERANCE_PENNIES,
} from 'src/utils/invoiceMatchToleranceHelpers'
import { saveToleranceSettings } from 'src/utils/invoiceMatchToleranceApi'

const InvoiceMatchToleranceModal = ({
  visible,
  venueId,
  settings,
  loading,
  onCancel,
  onSaved,
}) => {
  const [draft, setDraft] = useState(() => buildToleranceModalDraft(settings))
  const [saving, setSaving] = useState(false)
  const [supplierFilter, setSupplierFilter] = useState('')

  useEffect(() => {
    if (visible) {
      setDraft(buildToleranceModalDraft(settings))
      setSupplierFilter('')
    }
  }, [visible, settings])

  const venueDefaultLabel = formatTolerancePounds(
    Math.round(Number(draft.venueTolerancePounds || 0) * 100),
  )

  const filteredSuppliers = (draft.suppliers || []).filter(supplier => {
    const query = supplierFilter.trim().toLowerCase()
    if (!query) return true
    return String(supplier.supplierTitle || '')
      .toLowerCase()
      .includes(query)
  })

  const handleSave = async () => {
    try {
      const plan = buildToleranceSavePlan(draft, settings || {})
      if (plan.venuePennies === undefined && !plan.supplierUpdates.length) {
        onCancel?.()
        return
      }

      setSaving(true)
      const { settings: refreshed, rematch } = await saveToleranceSettings(
        venueId,
        plan,
      )
      const rematchedCount = rematch?.rematchedInvoiceIds?.length || 0
      const checkedCount = rematch?.checked || 0
      SuccessNotification({
        message: 'Match tolerance updated',
        description:
          rematchedCount > 0
            ? `Updated matching for ${rematchedCount} open invoice${
                rematchedCount === 1 ? '' : 's'
              }.`
            : checkedCount > 0
            ? 'Open invoices re-checked — matching outcomes unchanged.'
            : 'Smart matching will use these amounts when comparing invoice and order totals.',
      })
      onSaved?.(refreshed, rematch)
    } catch (error) {
      RejectedNotification({
        message: 'Could not save match tolerance',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title='Invoice match tolerance'
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okText='Save'
      confirmLoading={saving}
      destroyOnClose
      width={640}
    >
      {loading && !settings ? (
        <Spin />
      ) : (
        <ModalBody>
          <HelpText>
            Matching allows a small difference between the invoice total and the
            OMS order total. Set a venue default, and optionally override it per
            supplier (0 = exact match required). Max{' '}
            {formatTolerancePounds(MAX_INVOICE_MATCH_TOLERANCE_PENNIES)}.
          </HelpText>

          <FieldBlock>
            <FieldLabel>Venue default</FieldLabel>
            <InputNumber
              min={0}
              max={MAX_INVOICE_MATCH_TOLERANCE_PENNIES / 100}
              step={0.01}
              precision={2}
              value={draft.venueTolerancePounds}
              formatter={value => `£ ${value}`}
              parser={value => String(value || '').replace(/£\s?/g, '')}
              onChange={value =>
                setDraft(prev => ({
                  ...prev,
                  venueTolerancePounds: value,
                }))
              }
              style={{ width: 160 }}
            />
          </FieldBlock>

          <FieldBlock>
            <FieldLabel>Supplier overrides</FieldLabel>
            <Input
              placeholder='Filter suppliers'
              value={supplierFilter}
              onChange={e => setSupplierFilter(e.target.value)}
              allowClear
            />
            <SupplierList>
              {filteredSuppliers.length === 0 ? (
                <EmptySuppliers>No suppliers found.</EmptySuppliers>
              ) : (
                filteredSuppliers.map(supplier => {
                  const usesOverride = supplier.overridePounds != null
                  return (
                    <SupplierRow key={supplier.venueSupplierId}>
                      <SupplierMeta>
                        <SupplierTitle title={supplier.supplierTitle}>
                          {supplier.supplierTitle}
                        </SupplierTitle>
                        <SupplierEffective>
                          Effective:{' '}
                          {usesOverride
                            ? formatTolerancePounds(
                                Math.round(
                                  Number(supplier.overridePounds) * 100,
                                ),
                              )
                            : `${venueDefaultLabel} (venue default)`}
                        </SupplierEffective>
                      </SupplierMeta>
                      <SupplierControls>
                        <InputNumber
                          min={0}
                          max={MAX_INVOICE_MATCH_TOLERANCE_PENNIES / 100}
                          step={0.01}
                          precision={2}
                          placeholder='Default'
                          value={
                            supplier.overridePounds == null
                              ? undefined
                              : supplier.overridePounds
                          }
                          formatter={value =>
                            value === '' || value == null ? '' : `£ ${value}`
                          }
                          parser={value =>
                            String(value || '').replace(/£\s?/g, '')
                          }
                          onChange={value =>
                            setDraft(prev => ({
                              ...prev,
                              suppliers: prev.suppliers.map(row =>
                                row.venueSupplierId === supplier.venueSupplierId
                                  ? {
                                      ...row,
                                      overridePounds:
                                        value === null || value === undefined
                                          ? null
                                          : value,
                                    }
                                  : row,
                              ),
                            }))
                          }
                          style={{ width: 120 }}
                        />
                        {usesOverride && (
                          <Button
                            size='small'
                            onClick={() =>
                              setDraft(prev => ({
                                ...prev,
                                suppliers: prev.suppliers.map(row =>
                                  row.venueSupplierId ===
                                  supplier.venueSupplierId
                                    ? { ...row, overridePounds: null }
                                    : row,
                                ),
                              }))
                            }
                          >
                            Use default
                          </Button>
                        )}
                      </SupplierControls>
                    </SupplierRow>
                  )
                })
              )}
            </SupplierList>
          </FieldBlock>
        </ModalBody>
      )}
    </Modal>
  )
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
  line-height: 1.5;
`

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FieldLabel = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const SupplierList = styled.div`
  max-height: 320px;
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
`

const SupplierRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
`

const SupplierMeta = styled.div`
  min-width: 0;
  flex: 1;
`

const SupplierTitle = styled.div`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SupplierEffective = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`

const SupplierControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const EmptySuppliers = styled.div`
  padding: 16px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
`

export default InvoiceMatchToleranceModal
